import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useFragment } from 'react-relay/hooks';
import { isEmpty } from 'lodash';

import { Theme } from '@material-ui/core';
import CardHeader, { CardHeaderProps } from '@material-ui/core/CardHeader';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { graphql } from 'babel-plugin-relay/macro';
import classnames from 'classnames';
import moment from 'moment';

import { RolloutEnum } from '@lib/resources/types';
import { useCurrentUser } from '@lib/hooks';

import { ButtonPatientMerge, Card, CardProps } from 'components/v2';
import usePermissions from 'hooks/usePermissions';

import { CardPatient_Patient$key } from './__generated__/CardPatient_Patient.graphql';
import AvatarNode from './AvatarNode';

// TODO extract atoms/fragments from CardPatient component
const styles = ({ palette: { text }, spacing: { unit } }: Theme) =>
  createStyles({
    patient: {},
    root: {
      borderRadius: 0,
      overflow: 'visible',
      position: 'relative',
      '&$patient': {
        boxShadow: 'none',
      },
    },
    subheader: {
      color: text.secondary,
      fontSize: '12px !important',
      fontWeight: 'inherit',
      lineHeight: '18px',
      display: 'flex',
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      minHeight: 30,
      width: '100%',
    },
    secondaryTitle: {
      display: 'flex',
      width: '100%',
    },
    contactWrapper: {
      marginLeft: -1 * unit,
    },
    titleMain: {
      display: 'inline-block',
      fontSize: 15,
      fontWeight: 500,
      lineHeight: '24px',
      whiteSpace: 'nowrap',
    },
    marginLeft: {
      marginLeft: unit,
    },
  });

const messages = defineMessages({
  loggedInfoNeverLogged: {
    id: 'deprecated.fragments.CardPatient.loggedInfoNeverLogged',
    defaultMessage: 'never logged in',
  },
  loggedInfoPatient: {
    id: 'deprecated.fragments.CardPatient.loggedInfoPatient',
    defaultMessage: 'Last seen: {info}',
  },
  secondaryTextDOB: {
    id: 'deprecated.fragments.CardPatient.secondaryTextDOB',
    defaultMessage: 'DOB: {info}',
  },
  mrn: {
    id: 'deprecated.fragments.CardPatient.mrn',
    defaultMessage: 'MRN: {info}',
  },
});

export interface CardPatientProps extends Omit<CardProps, 'classes'>, WithStyles<typeof styles> {
  fragmentRef: CardPatient_Patient$key;
  includePatientMerge?: boolean;
  CardHeaderAction?: CardHeaderProps['action'];
  CardHeaderPatientContact?: React.ReactNode;
}

const CardPatient: React.FC<CardPatientProps> = ({
  classes,
  fragmentRef,
  includePatientMerge,
  CardHeaderAction,
  CardHeaderPatientContact,
  ...rest
}) => {
  const patient = useFragment<CardPatient_Patient$key>(
    graphql`
      fragment CardPatient_Patient on Patient {
        ...AvatarNode_Node
        _id
        name
        firstname
        lastname
        medicalRecordNumber
        createdAt
        lastLogin
        dateOfBirth {
          day
          month
          year
        }
      }
    `,
    fragmentRef,
  );
  const { formatMessage } = useIntl();
  const { hasAccessToProduct } = usePermissions();
  const { firstname, lastname, createdAt, lastLogin, dateOfBirth, medicalRecordNumber } = patient!;
  const currentUser = useCurrentUser();
  const shouldHideMRNInformation = !!currentUser?.settings?.patients?.hideMedicalRecordNumber?.enabled;
  const hasMRNCustomLabelSettingEnabled = !!currentUser?.settings?.patients?.medicalRecordNumberCustomLabel?.enabled;
  const customLabelMRN = currentUser?.settings?.patients?.medicalRecordNumberCustomLabel?.customLabel;
  const shouldDisplayMRNCustomLabel = hasMRNCustomLabelSettingEnabled && !isEmpty(customLabelMRN);

  const { day, month, year } = dateOfBirth as any;

  const birthDate = moment(`${year}-${month}-${day}`, 'YYYY-MM-DD').format('L');
  const lastSeen = moment(lastLogin as string).isSame(moment(createdAt as string))
    ? formatMessage(messages.loggedInfoNeverLogged)
    : moment(lastLogin as string).format('MM/DD h:mma');

  return (
    <Card className={classes.root} {...rest}>
      <CardHeader
        avatar={<AvatarNode fragmentRef={patient} />}
        disableTypography
        title={
          <div className={classes.title}>
            <Typography data-lh-id="patient-name-text" className={classes.titleMain} color="primary" component="span">
              {lastname}, {firstname}
            </Typography>
            {!shouldHideMRNInformation && (
              <Typography className={classes.marginLeft} component="span" inline>
                {medicalRecordNumber &&
                  (shouldDisplayMRNCustomLabel
                    ? `${customLabelMRN}: ${medicalRecordNumber}`
                    : formatMessage(messages.mrn, { info: medicalRecordNumber }))}
              </Typography>
            )}
            {includePatientMerge && hasAccessToProduct(RolloutEnum.PatientMerge) && (
              <ButtonPatientMerge
                className={classes.marginLeft}
                patientId={patient._id}
                variant="outlined"
                color="supportSteel"
                size="small"
                svgIconWrapperProps={{ fontSize: 'small' }}
              />
            )}
          </div>
        }
        subheader={
          <div>
            <Typography className={classnames(classes.secondaryTitle, classes.subheader)} component="div">
              {formatMessage(messages.secondaryTextDOB, { info: birthDate })} -{' '}
              {formatMessage(messages.loggedInfoPatient, { info: lastSeen })}
            </Typography>
            <Typography
              className={classnames(classes.secondaryTitle, classes.subheader, classes.contactWrapper)}
              component="div"
            >
              {CardHeaderPatientContact}
            </Typography>
          </div>
        }
        action={CardHeaderAction}
      />
    </Card>
  );
};

export default withStyles(styles)(CardPatient);
